'use client';
import type { Program } from '@/modules/Entertainment/interface/Program';
import { FC, useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { useQueryClient } from '@tanstack/react-query';
import { Flex } from '@eqi-investimentos/react';
import {
  EDUCATIONAL_CONTENT_TYPES,
  ENTERTAINMENT_CONTENT_TYPES,
} from '@/modules/Entertainment/interface/Content';
import { useT } from '@/libs/i18n/utils/useT';
import { appConfig } from '@/config/app';
import { PATHS } from '@/config/paths';
import { BannerTopHorizontal } from '@/components/modules/BannerTopHorizontal';
import { useGetV1ProgramSeasons } from '@/modules/Entertainment/http/client/useGetV1ProgramSeasons';
import { IfRender } from '@/utils/jsx';
import { useGetCTAProgram } from '../../http/client/useGetCTAProgram';
import { useGetProgram } from '../../http/client/useGetProgram';
import { ClassesSection } from './ClassesSection';
import { InfoData } from './InfoData';
import { MaterialSection } from './MaterialSection';

export const ProgramHub: FC<ProgramHubProps> = ({
  isEqi,
  program: initialProgram,
  slug,
}) => {
  const { data: session, status } = useSession();
  const hasSession = status === 'authenticated' && !session?.error;

  const queryClient = useQueryClient();

  const t = useT();

  const { data: program = initialProgram } = useGetProgram(slug);
  const { data: seasons = [], isFetching: isFetchingSeasons } =
    useGetV1ProgramSeasons(slug);

  const { data: dataCTAProgram } = useGetCTAProgram(slug);

  const [programType, setProgramType] = useState({
    isEducational: false,
    isEntertainment: false,
    isResearch: false,
  });

  const [label, setLabel] = useState(
    `${t('attributes.toStart')} ${t('attributes.o')} ${t('attributes.course')}`
  );

  const handleProgramType = (type: string) => {
    const isEntertainment = ENTERTAINMENT_CONTENT_TYPES.includes(type);
    const isEducational = EDUCATIONAL_CONTENT_TYPES.includes(type);

    setProgramType({
      isEducational,
      isEntertainment,
      isResearch: !isEntertainment && !isEducational,
    });

    if (isEntertainment) {
      setLabel(`${t('attributes.play')}`);
      return;
    }

    if (!isEducational) {
      setLabel(`${t('attributes.toStart')}`);
    }
  };

  useEffect(() => {
    handleProgramType(initialProgram?.type);
    queryClient.setQueryData(['program', slug], initialProgram);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProgram]);

  return (
    <Flex direction="column" gap="6xl">
      <Flex w="full" h="32rem">
        <BannerTopHorizontal
          actions={[
            {
              title: label,
              href: `${PATHS.hubs.content}/${program.begin_content.slug}`,
              icon: 'PlayCircleOutline',
            },
            {
              title: program.bookmarked
                ? t('attributes.saved')
                : t('attributes.save'),
              action: {
                type: 'bookmark',
                id: program.id,
                currentBookmarked: !!program.bookmarked,
              },
              icon: program.bookmarked
                ? 'BookmarkRemoveOutline'
                : 'BookmarkAddOutline',
              buttonStyle: 'solid',
              hasSpinner: true,
              hidden: !hasSession,
            },
            {
              title: 'Compartilhar',
              action: {
                type: 'share',
                href: `${appConfig.appURL}${PATHS.hubs.program}/${program?.slug}`,
              },
              icon: 'ShareOutline',
              buttonStyle: 'naked',
              hasSpinner: false,
            },
          ]}
          bg_image={program.bg_desktop ?? ''}
          description={program.summary ?? ''}
          qtd_content={String(program.content_count ?? '')}
          thumb={program.logo}
          time_duration={program.view_time ?? ''}
          refreshData={() =>
            queryClient.refetchQueries({
              queryKey: ['program', slug],
            })
          }
        />
      </Flex>
      <Flex
        flexDir="column"
        w="full"
        maxW="90.625rem"
        px="2xl"
        gap="6xl"
        mb="6xl"
        color="content.primary"
      >
        <InfoData
          program={program}
          isEqi={isEqi}
          dataCTAProgram={dataCTAProgram}
        />
        <IfRender condition={!isFetchingSeasons}>
          <ClassesSection seasons={seasons} isLoading={isFetchingSeasons} />
        </IfRender>
        <IfRender condition={programType.isEducational}>
          <MaterialSection programFiles={program?.files || []} />
        </IfRender>
      </Flex>
    </Flex>
  );
};

type ProgramHubProps = {
  isEqi: boolean;
  program: Program;
  slug: string;
};
